import { computed, ref, watch, type ComputedRef } from 'vue'
import { ElMessage } from 'element-plus'
import { channelOptions, createGroups } from './constants'
import type { ReceiverGroup, StationRow } from './types'

/**
 * 推送接收人分组状态：分组增删改、成员/电站管理、各操作对话框。
 * @param stations 全部电站列表（用于组名/可选电站计算），由上层注入以保证单例共享。
 */
export function useReceivers(stations: ComputedRef<StationRow[]>) {
  const groups = ref<ReceiverGroup[]>(createGroups())

  let groupSeq = 5

  function getStationName(sid: string) {
    const s = stations.value.find((x) => x.id === sid)
    return s ? s.name : sid
  }

  // ==================== 新建组对话框 ====================
  const newGroupDialogOpen = ref(false)
  const newGroupName = ref('')
  const newGroupNameError = ref<string[]>([])

  function openNewGroupDialog() {
    newGroupName.value = ''
    newGroupNameError.value = []
    newGroupDialogOpen.value = true
  }

  function closeNewGroupDialog() {
    newGroupDialogOpen.value = false
  }

  watch(newGroupDialogOpen, (open) => {
    if (!open) {
      newGroupName.value = ''
      newGroupNameError.value = []
    }
  })

  function onNewGroupNameInput() {
    newGroupNameError.value = []
  }

  function confirmNewGroup() {
    const value = newGroupName.value.trim()
    if (!/\S+/.test(value)) {
      newGroupNameError.value = ['组名不能为空']
      return
    }
    newGroupNameError.value = []
    groups.value.push({
      id: 'g' + groupSeq++,
      name: value,
      members: [],
      stations: [],
      channels: { supervisor: true, app: false, web: false },
      _newMember: '',
    })
    ElMessage.success(`接收组「${value}」已创建`)
    closeNewGroupDialog()
  }

  // ==================== 重命名对话框 ====================
  const renameGroupDialogOpen = ref(false)
  const renameGroupIndex = ref<number | null>(null)
  const renameGroupName = ref('')
  const renameGroupNameError = ref<string[]>([])

  function openRenameGroupDialog(gi: number) {
    const g = groups.value[gi]
    if (!g) return
    renameGroupIndex.value = gi
    renameGroupName.value = g.name
    renameGroupNameError.value = []
    renameGroupDialogOpen.value = true
  }

  function closeRenameGroupDialog() {
    renameGroupDialogOpen.value = false
  }

  watch(renameGroupDialogOpen, (open) => {
    if (!open) {
      renameGroupIndex.value = null
      renameGroupName.value = ''
      renameGroupNameError.value = []
    }
  })

  function onRenameGroupNameInput() {
    renameGroupNameError.value = []
  }

  function confirmRenameGroup() {
    const gi = renameGroupIndex.value
    if (gi === null || gi < 0 || !groups.value[gi]) {
      closeRenameGroupDialog()
      return
    }
    const value = renameGroupName.value.trim()
    if (!/\S+/.test(value)) {
      renameGroupNameError.value = ['组名不能为空']
      return
    }
    renameGroupNameError.value = []
    groups.value[gi].name = value
    ElMessage.success('组名已修改')
    closeRenameGroupDialog()
  }

  // ==================== 删除对话框 ====================
  const removeGroupDialogOpen = ref(false)
  const removeGroupIndex = ref<number | null>(null)

  const removeGroupTargetName = computed(() => {
    const i = removeGroupIndex.value
    if (i === null || i < 0 || !groups.value[i]) return ''
    return groups.value[i].name
  })

  function openRemoveGroupDialog(gi: number) {
    if (!groups.value[gi]) return
    removeGroupIndex.value = gi
    removeGroupDialogOpen.value = true
  }

  function closeRemoveGroupDialog() {
    removeGroupDialogOpen.value = false
  }

  watch(removeGroupDialogOpen, (open) => {
    if (!open) removeGroupIndex.value = null
  })

  function confirmRemoveGroup() {
    const gi = removeGroupIndex.value
    if (gi === null || gi < 0 || !groups.value[gi]) {
      closeRemoveGroupDialog()
      return
    }
    groups.value.splice(gi, 1)
    ElMessage.success('已删除')
    closeRemoveGroupDialog()
  }

  // ==================== 成员管理 ====================
  function addMember(gi: number) {
    const m = groups.value[gi]._newMember.trim()
    if (!m) return
    if (groups.value[gi].members.includes(m)) {
      ElMessage.warning('该工号已存在')
      return
    }
    groups.value[gi].members.push(m)
    groups.value[gi]._newMember = ''
  }

  function removeMember(gi: number, mi: number) {
    groups.value[gi].members.splice(mi, 1)
  }

  // ==================== 电站关联管理 ====================
  function addStationToGroup(gi: number, sid: string) {
    if (!sid) return
    if (!groups.value[gi].stations.includes(sid)) {
      groups.value[gi].stations.push(sid)
    }
  }

  function onPickStationForGroup(gi: number, sid: string) {
    addStationToGroup(gi, sid)
  }

  function removeStationFromGroup(gi: number, si: number) {
    groups.value[gi].stations.splice(si, 1)
  }

  function availableStationsForGroup(gid: string) {
    const g = groups.value.find((x) => x.id === gid)
    if (!g) return stations.value
    return stations.value.filter((s) => !g.stations.includes(s.id.toString()))
  }

  return {
    groups,
    channelOptions,
    getStationName,
    // 新建组对话框
    newGroupDialogOpen,
    newGroupName,
    newGroupNameError,
    openNewGroupDialog,
    closeNewGroupDialog,
    onNewGroupNameInput,
    confirmNewGroup,
    // 重命名对话框
    renameGroupDialogOpen,
    renameGroupName,
    renameGroupNameError,
    openRenameGroupDialog,
    closeRenameGroupDialog,
    onRenameGroupNameInput,
    confirmRenameGroup,
    // 删除对话框
    removeGroupDialogOpen,
    removeGroupTargetName,
    openRemoveGroupDialog,
    closeRemoveGroupDialog,
    confirmRemoveGroup,
    // 成员管理
    addMember,
    removeMember,
    // 电站关联管理
    addStationToGroup,
    onPickStationForGroup,
    removeStationFromGroup,
    availableStationsForGroup,
  }
}
